import React, { Component } from 'react';
import { ColorSwatch } from '../components/ColorSwatch';
import { TypographyScale } from '../components/TypographyScale';
import { ComponentShowcase } from '../components/ComponentShowcase';
import { InstagramPost } from '../components/InstagramPost';
import { InstagramGallery } from '../components/InstagramGallery';
import { Camera, Download, Share2, MapPin } from 'lucide-react';
export function BrandBook() {
  return <div className="min-h-screen bg-film-cream text-film-black pb-20" data-id="element-378">
      {/* Hero Section */}
      <header className="bg-film-black text-white py-20 px-6" data-id="element-379">
        <div className="max-w-6xl mx-auto" data-id="element-380">
          <div className="flex items-center gap-6 mb-6" data-id="element-381">
            <img src="https://cdn.magicpatterns.com/uploads/6S42RJUThA4LVcV5MgRYxz/filmnotes-fn-full-icon_1024.png" alt="Film Notes Icon" className="w-20 h-20" data-id="element-382" />
            <img src="https://cdn.magicpatterns.com/uploads/62MYaGZbQajYDp7mxVZnVB/filmnotes-longtext-icon-redwhite.png" alt="Film Notes" className="h-16" data-id="element-383" />
          </div>
          <p className="text-xl md:text-2xl text-gray-400 max-w-2xl" data-id="element-384">
            Brand Guidelines & Design System
          </p>
          <div className="mt-8 flex gap-4" data-id="element-385">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 text-sm border border-white/20" data-id="element-386">
              v1.0.0
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 text-sm border border-white/20" data-id="element-387">
              Updated Dec 2025
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 -mt-10" data-id="element-388">
        {/* Design Principles */}
        <section className="bg-white p-8 rounded-lg shadow-sm border border-film-border mb-12" data-id="element-389">
          <h2 className="text-sm font-display font-bold text-film-gray uppercase tracking-wider mb-6" data-id="element-390">
            Design Principles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-391">
            <div data-id="element-392">
              <h3 className="text-xl font-display font-bold mb-2" data-id="element-393">
                Analog Utility
              </h3>
              <p className="text-film-gray text-sm leading-relaxed" data-id="element-394">
                The interface mimics the tactile, utilitarian nature of film
                photography. It is not decorative; it is a tool. We use solid
                colors, clear borders, and high contrast.
              </p>
            </div>
            <div data-id="element-395">
              <h3 className="text-xl font-display font-bold mb-2" data-id="element-396">
                Information Density
              </h3>
              <p className="text-film-gray text-sm leading-relaxed" data-id="element-397">
                Photographers need data. ISO, aperture, shutter speed. We
                present dense information clearly through strict hierarchy and
                consistent spacing.
              </p>
            </div>
            <div data-id="element-398">
              <h3 className="text-xl font-display font-bold mb-2" data-id="element-399">
                Status & State
              </h3>
              <p className="text-film-gray text-sm leading-relaxed" data-id="element-400">
                Color is used primarily to indicate state (Loaded, Expired,
                Stored). Red is our primary action color, but also signals
                urgency.
              </p>
            </div>
          </div>
        </section>

        {/* Logo Section */}
        <section className="mb-20" data-id="element-401">
          <h2 className="text-3xl font-display font-bold mb-8" data-id="element-402">
            Logo & Brand Marks
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-403">
            {/* Full Logo Lockups */}
            <div className="bg-white p-8 rounded-lg border border-film-border" data-id="element-404">
              <h3 className="text-sm font-display font-bold text-film-gray uppercase tracking-wider mb-4" data-id="element-405">
                Primary Logo (Light Background)
              </h3>
              <div className="bg-film-cream p-8 rounded flex items-center justify-center" data-id="element-406">
                <img src="https://cdn.magicpatterns.com/uploads/n83aYeAfPgByVNUDm9vCqj/filmnotes-longtext-icon.png" alt="Film Notes Logo" className="h-16" data-id="element-407" />
              </div>
              <p className="text-xs text-film-gray mt-4" data-id="element-408">
                Use on light backgrounds
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-film-border" data-id="element-409">
              <h3 className="text-sm font-display font-bold text-film-gray uppercase tracking-wider mb-4" data-id="element-410">
                Primary Logo (Dark Background)
              </h3>
              <div className="bg-film-black p-8 rounded flex items-center justify-center" data-id="element-411">
                <img src="https://cdn.magicpatterns.com/uploads/62MYaGZbQajYDp7mxVZnVB/filmnotes-longtext-icon-redwhite.png" alt="Film Notes Logo White" className="h-16" data-id="element-412" />
              </div>
              <p className="text-xs text-film-gray mt-4" data-id="element-413">
                Use on dark backgrounds
              </p>
            </div>

            {/* Icon Variations */}
            <div className="bg-white p-8 rounded-lg border border-film-border" data-id="element-414">
              <h3 className="text-sm font-display font-bold text-film-gray uppercase tracking-wider mb-4" data-id="element-415">
                App Icon Variations
              </h3>
              <div className="grid grid-cols-3 gap-4" data-id="element-416">
                <div className="text-center" data-id="element-417">
                  <div className="bg-film-cream p-4 rounded-lg mb-2" data-id="element-418">
                    <img src="https://cdn.magicpatterns.com/uploads/6S42RJUThA4LVcV5MgRYxz/filmnotes-fn-full-icon_1024.png" alt="Full Color Icon" className="w-full" data-id="element-419" />
                  </div>
                  <p className="text-xs text-film-gray" data-id="element-420">Full Color</p>
                </div>
                <div className="text-center" data-id="element-421">
                  <div className="bg-film-cream p-4 rounded-lg mb-2" data-id="element-422">
                    <img src="https://cdn.magicpatterns.com/uploads/oTP2o8fSQ2isLxJ5nM9Smk/filmnotes-fn-red-icon_1024.png" alt="Red Icon" className="w-full" data-id="element-423" />
                  </div>
                  <p className="text-xs text-film-gray" data-id="element-424">Red</p>
                </div>
                <div className="text-center" data-id="element-425">
                  <div className="bg-film-cream p-4 rounded-lg mb-2" data-id="element-426">
                    <img src="https://cdn.magicpatterns.com/uploads/5ceYjttcNKtrLpuLGcysyS/filmnotes-fn-black-icon_1024.png" alt="Black Icon" className="w-full" data-id="element-427" />
                  </div>
                  <p className="text-xs text-film-gray" data-id="element-428">Black</p>
                </div>
              </div>
            </div>

            {/* Usage Guidelines */}
            <div className="bg-white p-8 rounded-lg border border-film-border" data-id="element-429">
              <h3 className="text-sm font-display font-bold text-film-gray uppercase tracking-wider mb-4" data-id="element-430">
                Logo Usage Guidelines
              </h3>
              <div className="space-y-3 text-sm" data-id="element-431">
                <div className="flex items-start gap-2" data-id="element-432">
                  <span className="text-film-red font-bold" data-id="element-433">✓</span>
                  <p className="text-film-gray" data-id="element-434">
                    Maintain clear space around logo equal to the height of "fn"
                  </p>
                </div>
                <div className="flex items-start gap-2" data-id="element-435">
                  <span className="text-film-red font-bold" data-id="element-436">✓</span>
                  <p className="text-film-gray" data-id="element-437">
                    Use full color icon on light backgrounds
                  </p>
                </div>
                <div className="flex items-start gap-2" data-id="element-438">
                  <span className="text-film-red font-bold" data-id="element-439">✓</span>
                  <p className="text-film-gray" data-id="element-440">
                    Use red/white lockup on dark backgrounds
                  </p>
                </div>
                <div className="flex items-start gap-2 pt-3 border-t border-film-border" data-id="element-441">
                  <span className="text-film-gray font-bold" data-id="element-442">✗</span>
                  <p className="text-film-gray" data-id="element-443">
                    Don't stretch or distort the logo
                  </p>
                </div>
                <div className="flex items-start gap-2" data-id="element-444">
                  <span className="text-film-gray font-bold" data-id="element-445">✗</span>
                  <p className="text-film-gray" data-id="element-446">Don't change logo colors</p>
                </div>
                <div className="flex items-start gap-2" data-id="element-447">
                  <span className="text-film-gray font-bold" data-id="element-448">✗</span>
                  <p className="text-film-gray" data-id="element-449">Don't add effects or shadows</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Color Palette */}
        <section className="mb-20" data-id="element-450">
          <div className="flex items-baseline justify-between mb-8" data-id="element-451">
            <h2 className="text-3xl font-display font-bold" data-id="element-452">Color Palette</h2>
            <p className="text-film-gray" data-id="element-453">Click any swatch to copy HEX</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6" data-id="element-454">
            <ColorSwatch name="Film Red" hex="#C93B3B" usage="Primary Brand, CTAs, FAB" data-id="element-455" />
            <ColorSwatch name="Red Light" hex="#E85D5D" usage="Loaded Status, Accents" data-id="element-456" />
            <ColorSwatch name="Warning Orange" hex="#FF8C42" usage="Expiring Status, Alerts" data-id="element-457" />
            <ColorSwatch name="Film Black" hex="#2B2B2B" usage="Headings, Primary Text" data-id="element-458" />
            <ColorSwatch name="Film Cream" hex="#F5F5F0" usage="App Background" textColor="text-film-black" data-id="element-459" />
            <ColorSwatch name="Stored Pink" hex="#F5E5E5" usage="Stored Status Background" textColor="text-film-black" data-id="element-460" />
            <ColorSwatch name="Film Gray" hex="#9B9B9B" usage="Secondary Text, Icons" data-id="element-461" />
            <ColorSwatch name="Border Gray" hex="#E8E8E8" usage="Dividers, Card Borders" textColor="text-film-black" data-id="element-462" />
            <ColorSwatch name="Nav Black" hex="#1A1A1A" usage="Bottom Navigation Bar" data-id="element-463" />
          </div>
        </section>

        {/* Typography */}
        <section className="mb-20" data-id="element-464">
          <h2 className="text-3xl font-display font-bold mb-8" data-id="element-465">Typography</h2>
          <TypographyScale data-id="element-466" />
        </section>

        {/* Components */}
        <section className="mb-20" data-id="element-467">
          <h2 className="text-3xl font-display font-bold mb-8" data-id="element-468">
            UI Components
          </h2>
          <ComponentShowcase data-id="element-469" />
        </section>

        {/* Social Media Examples - NEW 3:4 FORMAT */}
        <section className="mb-20" data-id="element-470">
          <div className="flex items-baseline justify-between mb-4" data-id="element-471">
            <div data-id="element-472">
              <h2 className="text-3xl font-display font-bold mb-2" data-id="element-473">
                Social Media Assets
              </h2>
              <p className="text-film-gray" data-id="element-474">
                Instagram Posts • 3:4 Aspect Ratio (1350×1080)
              </p>
            </div>
            <button className="flex items-center gap-2 text-sm font-display font-bold text-film-red hover:text-red-700 transition-colors" data-id="element-475">
              <Download className="w-4 h-4" data-id="element-476" /> Download All
            </button>
          </div>

          <div className="mb-8 p-6 bg-white border border-film-border rounded-lg" data-id="element-477">
            <h3 className="font-display font-bold text-film-black mb-3" data-id="element-478">
              Content Strategy
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm" data-id="element-479">
              <div data-id="element-480">
                <p className="font-display font-bold text-film-black mb-1" data-id="element-481">
                  Educational
                </p>
                <p className="text-film-gray" data-id="element-482">
                  Tips, techniques, camera guides
                </p>
              </div>
              <div data-id="element-483">
                <p className="font-display font-bold text-film-black mb-1" data-id="element-484">
                  Community
                </p>
                <p className="text-film-gray" data-id="element-485">User features, shared moments</p>
              </div>
              <div data-id="element-486">
                <p className="font-display font-bold text-film-black mb-1" data-id="element-487">
                  Product
                </p>
                <p className="text-film-gray" data-id="element-488">
                  App features, updates, benefits
                </p>
              </div>
              <div data-id="element-489">
                <p className="font-display font-bold text-film-black mb-1" data-id="element-490">
                  Inspiration
                </p>
                <p className="text-film-gray" data-id="element-491">Quotes, philosophy, motivation</p>
              </div>
            </div>
          </div>

          <InstagramGallery data-id="element-492" />
        </section>
      </main>

      <footer className="bg-film-black text-film-gray py-12 text-center" data-id="element-493">
        <p className="mb-2" data-id="element-494">Film Notes Design System</p>
        <p className="text-xs opacity-50" data-id="element-495">
          © 2025 Film Notes App. All rights reserved.
        </p>
      </footer>
    </div>;
}